import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { API } from '../../../lib/api';
import { toast } from 'sonner';
import {
  RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle,
  MessageSquare, Bell, Mail, User, Calendar, Eye, RotateCcw
} from 'lucide-react';

// Status badge
const StatusBadge = ({ status }) => {
  const styles = {
    delivered: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };
  
  const icons = {
    delivered: CheckCircle,
    failed: XCircle,
    cancelled: AlertTriangle,
  };
  
  const Icon = icons[status] || Clock;
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${styles[status] || 'bg-gray-100 text-gray-500'}`}>
      <Icon className="w-3 h-3" />
      {status === 'delivered' ? 'Consegnato' : status === 'failed' ? 'Fallito' : status}
    </span>
  );
};

// Channel icon
const ChannelIcon = ({ channel }) => {
  const icons = {
    whatsapp: MessageSquare,
    push: Bell,
    email: Mail,
  };
  const Icon = icons[channel] || MessageSquare;
  return <Icon className="w-4 h-4" />;
};

// Log Row
const LogRow = ({ log, onRetry, onViewDetails }) => {
  return (
    <div className="flex items-center justify-between p-4 hover:bg-gray-50 border-b border-gray-100 transition-colors">
      <div className="flex items-center gap-4 min-w-0 flex-1">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          log.status === 'delivered' ? 'bg-green-100' : log.status === 'failed' ? 'bg-red-100' : 'bg-gray-100'
        }`}>
          <ChannelIcon channel={log.channel} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {log.guest_name || log.guest_id}
          </p>
          <p className="text-xs text-gray-500">
            {log.template_name || 'Template sconosciuto'}
            {log.booking_code && ` • ${log.booking_code}`}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="text-right text-sm hidden md:block">
          <p className="text-gray-900">
            {log.created_at && new Date(log.created_at).toLocaleString('it-IT', {
              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
            })}
          </p>
          {log.execution_time_ms && (
            <p className="text-xs text-gray-500">{log.execution_time_ms}ms</p>
          )}
        </div>
        
        <StatusBadge status={log.status} />
        
        <div className="flex gap-1">
          <button
            onClick={() => onViewDetails(log)}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            title="Dettagli"
          >
            <Eye className="w-4 h-4 text-gray-600" />
          </button>
          {log.status === 'failed' && (
            <button
              onClick={() => onRetry(log.id)}
              className="p-2 hover:bg-orange-100 rounded-lg transition-colors"
              title="Ritenta"
            >
              <RotateCcw className="w-4 h-4 text-orange-600" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Details Modal
const DetailsModal = ({ log, onClose }) => {
  if (!log) return null;
  
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 text-lg">Dettagli Invio</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <XCircle className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Stato</p>
              <StatusBadge status={log.status} />
            </div>
            <div>
              <p className="text-gray-500">Canale</p>
              <p className="text-gray-900 capitalize">{log.channel}</p>
            </div>
            <div>
              <p className="text-gray-500">Ospite</p>
              <p className="text-gray-900">{log.guest_name || log.guest_id}</p>
            </div>
            <div>
              <p className="text-gray-500">Prenotazione</p>
              <p className="text-gray-900">{log.booking_code || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Template</p>
              <p className="text-gray-900">{log.template_name || log.template_id}</p>
            </div>
            <div>
              <p className="text-gray-500">Provider</p>
              <p className="text-gray-900">{log.provider_name || log.provider_id}</p>
            </div>
            <div>
              <p className="text-gray-500">Tentativi</p>
              <p className="text-gray-900">{log.retry_count || 0}</p>
            </div>
            <div>
              <p className="text-gray-500">Tempo esecuzione</p>
              <p className="text-gray-900">{log.execution_time_ms ? `${log.execution_time_ms}ms` : '-'}</p>
            </div>
          </div>

          {log.error_message && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs font-medium text-red-700 mb-1">Errore</p>
              <p className="text-sm text-red-800">{log.error_message}</p>
            </div>
          )}

          {log.provider_response && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-medium text-gray-700 mb-1">Risposta Provider</p>
              <pre className="text-xs text-gray-600 overflow-auto max-h-32">
                {JSON.stringify(log.provider_response, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function LogsPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = async () => {
    try {
      const url = filter 
        ? `${API}/admin/communication/logs?status=${filter}&limit=100`
        : `${API}/admin/communication/logs?limit=100`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Errore nel caricamento log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchLogs();
    }
  }, [token, filter]);

  const handleRetry = async (logId) => {
    try {
      const response = await fetch(`${API}/admin/communication/logs/${logId}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Retry programmato');
        fetchLogs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel retry');
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const handleRetryAll = async () => {
    if (!confirm('Ritentare tutti i messaggi falliti?')) return;
    
    try {
      const response = await fetch(`${API}/admin/communication/logs/retry-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchLogs();
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const statusFilters = [
    { value: '', label: 'Tutti' },
    { value: 'delivered', label: 'Consegnati' },
    { value: 'failed', label: 'Falliti' },
  ];

  const failedCount = logs.filter(l => l.status === 'failed').length;

  return (
    <AdminLayout title="Delivery Logs">
      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Log Invii</h1>
            <p className="text-gray-500 mt-1">Storico delle comunicazioni inviate</p>
          </div>
          <div className="flex gap-2">
            {failedCount > 0 && (
              <button
                onClick={handleRetryAll}
                className="px-4 py-2.5 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Ritenta tutti ({failedCount})
              </button>
            )}
            <button
              onClick={fetchLogs}
              className="p-2.5 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors shadow-sm"
              title="Aggiorna"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {statusFilters.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f.value 
                  ? 'bg-[#C5A059] text-white' 
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Logs List */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun log</h3>
              <p className="text-gray-500">I log delle comunicazioni appariranno qui</p>
            </div>
          ) : (
            <div>
              {logs.map(log => (
                <LogRow
                  key={log.id}
                  log={log}
                  onRetry={handleRetry}
                  onViewDetails={setSelectedLog}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <DetailsModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    </AdminLayout>
  );
}
